export type TranslationResource = Record<string, string>;

const toTranslationResource = (resource: unknown): TranslationResource => {
    if (resource === null || typeof resource !== "object" || Array.isArray(resource)) {
        return {};
    }

    return Object.fromEntries(
        Object.entries(resource).filter((entry): entry is [string, string] => typeof entry[1] === "string"),
    );
};

const invalidApiOverrides: Readonly<Record<string, readonly string[]>> = {
    "sidebar.transport.title": ["title"],
};

const isKnownInvalidApiOverride = (key: string, value: string): boolean =>
    invalidApiOverrides[key]?.includes(value.trim().toLocaleLowerCase()) ?? false;

const removeInvalidApiOverrides = (
    staticBase: TranslationResource,
    apiOverrides: TranslationResource,
): TranslationResource =>
    Object.fromEntries(
        Object.entries(apiOverrides).filter(([key, value]) => {
            return !(
                staticBase[key] !== undefined &&
                staticBase[key] !== value &&
                isKnownInvalidApiOverride(key, value)
            );
        }),
    );

/**
 * Bundled translations are the complete baseline. The API may override them
 * but an incomplete or stale API catalog must never remove a known key.
 */
export const mergeTranslationResources = (staticBase: unknown, apiOverrides: unknown): TranslationResource => {
    const bundledTranslations = toTranslationResource(staticBase);
    const apiTranslations = removeInvalidApiOverrides(bundledTranslations, toTranslationResource(apiOverrides));

    return {
        ...bundledTranslations,
        ...apiTranslations,
    };
};
